import { useCallback, useRef } from 'react';

const STORAGE_KEY = 'jerico_nav_usage';
const SUGGEST_THRESHOLD = 10;
const SUGGEST_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface UsageEntry {
  count: number;
  lastUsed: string;
  lastSuggested?: string;
}

type UsageData = Record<string, UsageEntry>;

function getUsageData(): UsageData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return {};
}

function saveUsageData(data: UsageData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
}

export function useNavUsage() {
  const dataRef = useRef<UsageData>(getUsageData());

  const recordClick = useCallback((path: string) => {
    const data = dataRef.current;
    const entry = data[path] || { count: 0, lastUsed: '' };
    entry.count += 1;
    entry.lastUsed = new Date().toISOString();
    data[path] = entry;
    dataRef.current = data;
    saveUsageData(data);
  }, []);

  const shouldSuggestPin = useCallback((path: string, isPinned: boolean) => {
    if (isPinned) return false;
    const data = dataRef.current;
    const entry = data[path];
    if (!entry || entry.count < SUGGEST_THRESHOLD) return false;

    // Check cooldown
    if (entry.lastSuggested) {
      const elapsed = Date.now() - new Date(entry.lastSuggested).getTime();
      if (elapsed < SUGGEST_COOLDOWN_MS) return false;
    }

    // Check recency (used in last 14 days)
    const daysSinceUse = (Date.now() - new Date(entry.lastUsed).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceUse > 14) return false;

    return true;
  }, []);

  const dismissSuggestion = useCallback((path: string) => {
    const data = dataRef.current;
    if (data[path]) {
      data[path].lastSuggested = new Date().toISOString();
      dataRef.current = data;
      saveUsageData(data);
    }
  }, []);

  return { recordClick, shouldSuggestPin, dismissSuggestion };
}
